/**
 * Fire browser downloads for a list of recordings.
 *
 * Two modes:
 *   1. Directory-picker mode (Chrome/Edge/desktop Safari 15.2+): calls
 *      `showDirectoryPicker()` before downloading and streams each file
 *      directly into that folder via the File System Access API. The
 *      user picks the destination ONCE instead of hunting through their
 *      Downloads folder after the fact.
 *   2. Fallback (iOS Safari, Firefox, etc.): triggers standard browser
 *      downloads via anchor clicks, spaced out so the browser doesn't
 *      throttle them. Files land in the browser's default Downloads
 *      folder.
 *
 * Also mirrors each successful download into localStorage via
 * `trackLocalDownload` so guest users (no Google Sign-in) still see
 * their downloads on the DownloadsPage.
 *
 * Feb-2026 (user "i think i just accidentaly started a download of all
 * recrodings to my folder please stop it"): every bulk download now
 * exposes a global cancel switch. `cancelActiveBulkDownload()` flips
 * a module-level flag that the download loop checks between files,
 * plus aborts the in-flight `fetch()` via AbortController so even a
 * huge MP3 mid-transfer stops immediately. `isBulkDownloadActive()`
 * lets any page render a persistent "Cancel download" pill so the
 * user never gets trapped in an unstoppable batch again.
 */
import { trackLocalDownload } from './localDownloads';

// ── Cancellable bulk-download infrastructure ──────────────────────
const _state = { active: false, cancelled: false, abort: null };
const _subs = new Set();
const _notify = () => _subs.forEach((cb) => { try { cb(); } catch (_) { /* ignore */ } });

export const subscribeBulkDownload = (cb) => {
  _subs.add(cb);
  return () => _subs.delete(cb);
};
export const isBulkDownloadActive = () => _state.active;
export const cancelActiveBulkDownload = () => {
  if (!_state.active) return false;
  _state.cancelled = true;
  try { _state.abort?.abort(); } catch (_) { /* ignore */ }
  return true;
};
const _startBulk = () => {
  _state.active = true;
  _state.cancelled = false;
  _state.abort = new AbortController();
  _notify();
};
const _endBulk = () => {
  _state.active = false;
  _state.cancelled = false;
  _state.abort = null;
  _notify();
};

const supportsDirectoryPicker = () =>
  typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

const safeFileName = (r, ext = 'mp3') => {
  const base = (r.title || r.drive_file_id || 'recording').replace(/[^\w\-. ]+/g, '_');
  return base.toLowerCase().endsWith(`.${ext}`) ? base : `${base}.${ext}`;
};

/**
 * Prompt the user to pick a destination directory and stream each file
 * into it. Returns the count of files written, or `null` if the user
 * cancelled or the browser doesn't support the picker (caller should
 * fall back to the anchor-click path).
 *
 * When `subfolder` is truthy on a recording (`r.folder_path`), the file
 * lands in `<picked>/<sanitised speaker>/<sanitised folder_path>/<title>.mp3`.
 * Set `opts.groupBySpeaker=false` to write everything flat.
 */
const _safeFolderPart = (s) => (s || '').replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim();

const _ensureNested = async (root, parts) => {
  let dir = root;
  for (const raw of parts) {
    const part = _safeFolderPart(raw);
    if (!part) continue;
    // eslint-disable-next-line no-await-in-loop
    dir = await dir.getDirectoryHandle(part, { create: true });
  }
  return dir;
};

const downloadIntoPickedFolder = async (list, opts = {}) => {
  const { onProgress, groupBySpeaker = true, groupByFolder = true } = opts;
  let dirHandle;
  try {
    dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
  } catch (e) {
    // User cancelled the picker or permission was denied.
    return null;
  }
  let written = 0;
  for (let i = 0; i < list.length; i += 1) {
    if (_state.cancelled) break;
    const r = list[i];
    const url = r.download_url || r.stream_url;
    if (!url) continue;
    try {
      const res = await fetch(url, { credentials: 'omit', signal: _state.abort?.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (_state.cancelled) break;
      const parts = [];
      if (groupBySpeaker && r.speaker) parts.push(r.speaker);
      if (groupByFolder && r.folder_path) {
        // Split on '/' so nested folders become nested subdirs
        r.folder_path.split('/').forEach((p) => p && parts.push(p));
      }
      const targetDir = parts.length ? await _ensureNested(dirHandle, parts) : dirHandle;
      const fh = await targetDir.getFileHandle(safeFileName(r), { create: true });
      const ws = await fh.createWritable();
      await ws.write(blob);
      await ws.close();
      written += 1;
      trackLocalDownload(r);
      if (onProgress) onProgress(i + 1, list.length);
    } catch (err) {
      if (err?.name === 'AbortError' || _state.cancelled) break;
      // Fall through — one bad recording shouldn't block the batch.
      // The caller sees `written` at the end and can retry.
      // eslint-disable-next-line no-console
      console.warn('download failed for', r.drive_file_id, err);
    }
  }
  return written;
};

const downloadViaAnchors = async (list, opts = {}) => {
  const { delayMs = 350, onProgress } = opts;
  for (let i = 0; i < list.length; i += 1) {
    if (_state.cancelled) break;
    const r = list[i];
    const url = r.download_url || r.stream_url;
    const a = document.createElement('a');
    a.href = url;
    a.download = safeFileName(r);
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    trackLocalDownload(r);
    if (onProgress) onProgress(i + 1, list.length);
    if (i < list.length - 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, delayMs));
    }
  }
  return list.length;
};

export const downloadRecordings = async (recordings, opts = {}) => {
  const list = (recordings || []).filter((r) => r && (r.download_url || r.stream_url));
  if (list.length === 0) return 0;

  // Feb-2026 (user request): if the user is downloading MORE than one
  // recording AND the browser supports `showDirectoryPicker`, prompt
  // them to pick a destination folder and save each recording as a
  // separate file — NO zipping. The single-file case still uses the
  // browser's built-in download (there is no folder ambiguity to solve).
  const { pickDestination = list.length > 1 } = opts;
  const bulk = list.length > 1;
  if (bulk) _startBulk();
  try {
    if (pickDestination && supportsDirectoryPicker()) {
      const written = await downloadIntoPickedFolder(list, opts);
      if (written !== null) return written;
      // User cancelled the picker — fall through to browser downloads.
    }
    return await downloadViaAnchors(list, opts);
  } finally {
    if (bulk) _endBulk();
  }
};

/**
 * Feb-2026 (user "Multi-Download No Zip"): fetch every recording for a
 * given speaker (or ALL speakers when speaker == null) and save each
 * one as an individual audio file into a folder the user picks. This
 * REPLACES the old `/api/export/speaker/{name}.zip` flow, which the
 * user explicitly asked to move away from. Falls back to sequential
 * browser downloads on Firefox / older Safari.
 *
 * Returns the number of files successfully written, or 0 on cancel.
 */
export const downloadSpeakerAsFiles = async (speaker, opts = {}) => {
  const { onProgress, onStatus } = opts;
  const API = process.env.REACT_APP_BACKEND_URL;
  const params = new URLSearchParams({ limit: '20000', fields: 'lite' });
  if (speaker) params.set('speaker', speaker);
  if (onStatus) onStatus('Loading recording list…');
  const res = await fetch(`${API}/api/recordings?${params.toString()}`);
  if (!res.ok) throw new Error(`recordings HTTP ${res.status}`);
  const recs = await res.json();
  const list = (recs || []).filter((r) => r && (r.download_url || r.stream_url));
  if (list.length === 0) return 0;
  if (onStatus) onStatus(`Downloading ${list.length.toLocaleString()} files…`);
  return downloadRecordings(list, {
    pickDestination: true,
    groupBySpeaker: !speaker, // only group when downloading multiple speakers
    groupByFolder: true,
    onProgress,
  });
};
